import { AxiosResponse } from "axios"
import { authenticate } from "@web-eid/web-eid-library/web-eid";
import { User, UserRole } from "../types/user"
import { getCsrfToken, getResponseData, jsonApi } from "./utils";
import { getLocale } from "../i18n"
import { DigiAuthUser } from "../types/user"
import { Iso3166Alpha2 } from "../types/countries"
import { trackGAEventforPHD, generateRegisterByEmailEventObject, generateLogInEventObject } from "../utils/trackGAEventforPHD";

export interface EmailUser {
  email: string
  password: string
  rememberMe: boolean
  role: UserRole
  termsOfUse: boolean
}

export interface UserEmail {
  email: string
}

export interface UserPassword {
  resetPasswordToken: string | null
  password: string
}

export interface FeedbackData {
  email: string;
  fullName: string;
  phoneCountryCode: Iso3166Alpha2;
  phoneNumber: string;
  message: string;
  rate: number | null;
}

declare type UserResponse = {
  user: User;
}

export interface StartDigiAuthResponse {
  code: string;
  sessionId: string;
}

function handleNewUserResponse(response: AxiosResponse<UserResponse>): User {
  if (response.data.user) App.user = response.data.user;
  return response.data.user
}

async function createUser(user: EmailUser): Promise<User> {
  const locale = getLocale()
  return jsonApi.post<UserResponse>("/internal_api/user", { locale, user })
  .then((response) => {
    trackGAEventforPHD(generateRegisterByEmailEventObject(response.data.user));
    trackGAEventforPHD(generateLogInEventObject(response.data.user));
    return handleNewUserResponse(response)
  })
}

async function getCurrentUser(): Promise<User> {
  return jsonApi.get<UserResponse>("/internal_api/user").then(response => response.data.user);
}

export interface DigiAuthResponse {
  state?: "running";
  user: User;
}

async function getDigiAuthUser(sessionId: string): Promise<DigiAuthResponse> {
  const locale = getLocale()
  return jsonApi.patch<DigiAuthResponse>(`/internal_api/digi_auth_users/${sessionId}`, { locale })
    .then(response => {
      if (response.data.user) App.user = response.data.user;
      trackGAEventforPHD(generateLogInEventObject(response.data.user));
      return response.data
    })
}

async function signIn(user: EmailUser): Promise<User> {
  const locale = getLocale()
  return jsonApi.post<UserResponse>("/internal_api/sessions", {locale, user})
    .then(response => {
      if (response.data.user.role !== 'customer_service') {
        trackGAEventforPHD(generateLogInEventObject(response.data.user));
      }
      return handleNewUserResponse(response)
    })
}

async function idCardAuth(user: DigiAuthUser): Promise<User> {
  const locale = getLocale();
  return authenticate(getCsrfToken(), { lang: locale })
    .then(authenticateData => {
      return jsonApi.post<StartDigiAuthResponse>("/internal_api/id_card_users", { authenticateData, locale, user })
    })
    .then(response => {
      return jsonApi.patch<UserResponse>(`/internal_api/digi_auth_users/${response.data.sessionId}`, { locale })
    })
    .then(handleNewUserResponse);
  }

async function startDigiAuth(user: DigiAuthUser): Promise<StartDigiAuthResponse> {
  const locale = getLocale()
  return jsonApi.post<StartDigiAuthResponse>("/internal_api/digi_auth_users", { locale, user })
    .then(getResponseData)
}

async function newPassword(user: UserEmail): Promise<User> {
  const locale = getLocale()
  return jsonApi.post<UserResponse>("/internal_api/passwords", { locale, user })
    .then(response => response.data.user)
}

async function updatePassword(user: UserPassword): Promise<User> {
  const locale = getLocale()
  return jsonApi.patch<UserResponse>("/internal_api/passwords", { locale, user })
    .then(response => response.data.user)
}

export interface FeedbackResponse {
  redirectUrl: string;
}

async function submitFeedback(feedback: FeedbackData): Promise<FeedbackResponse> {
  const locale = getLocale()
  return jsonApi.post<FeedbackResponse>("/internal_api/feedback", { locale, feedback })
    .then(getResponseData)
}

async function submitGuideViews(): Promise<void> {
  return jsonApi.post<void>("/internal_api/guide_views").then(getResponseData);
}

export default {
  createUser,
  getCurrentUser,
  getDigiAuthUser,
  idCardAuth,
  newPassword,
  updatePassword,
  signIn,
  startDigiAuth,
  submitFeedback,
  submitGuideViews,
}
